import { useInitiateOAuthMutation } from '@saaga/shared/store/connector-endpoints.store'
import { logger } from '@saaga/shared/utils/logger.util'
import { useCallback, useRef } from 'react'
import { toast } from 'sonner'
import { buildOAuthCallbackUrl } from '@/routes/oauth-callback/oauth-callback-url.util'
import { useOAuthPopup } from '@/routes/oauth-callback/use-oauth-popup.hook'

export interface UseConnectorOAuthReturn {
  handleConnect: (providerSlug: string) => void
  connectingProviderSlug: string | null
}

/**
 * Manages the OAuth connection initiation flow.
 * Tracks which specific provider is currently connecting.
 */
export const useConnectorOAuth = (): UseConnectorOAuthReturn => {
  const connectingProviderRef = useRef<string | null>(null)
  const [initiateOAuth, { isLoading: isInitiating }] = useInitiateOAuthMutation()
  const { openOAuthPopup, isPopupOpen } = useOAuthPopup(
    () => {
      connectingProviderRef.current = null
      toast.success('Successfully connected')
    },
    (error) => {
      connectingProviderRef.current = null
      logger.error('OAuth callback failed', error)
      toast.error('Failed to complete connection')
    }
  )

  const handleConnect = useCallback(
    async (providerSlug: string) => {
      if (connectingProviderRef.current) return
      connectingProviderRef.current = providerSlug

      try {
        const redirectUri = buildOAuthCallbackUrl()
        const result = await initiateOAuth({
          provider: providerSlug,
          redirectUri,
        }).unwrap()
        openOAuthPopup(result.authUrl, providerSlug)
      } catch (error) {
        connectingProviderRef.current = null
        logger.error('Failed to initiate OAuth', error)
        toast.error('Failed to start connection')
      }
    },
    [initiateOAuth, openOAuthPopup]
  )

  const isConnecting = isInitiating || isPopupOpen
  const connectingProviderSlug = isConnecting ? connectingProviderRef.current : null

  return { handleConnect, connectingProviderSlug }
}
